import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient, useAuth } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  Users, 
  Clock, 
  CalendarCheck, 
  Heart, 
  AlertCircle,
  Package,
  TrendingUp,
  TrendingDown,
  ChevronRight,
  Pill,
  UserCheck,
  Send,
  Building2,
  CreditCard,
  Video,
  ExternalLink,
  DollarSign
} from 'lucide-react';
import { toast } from 'sonner';

const DashboardPage = () => {
  const [data, setData] = useState(null);
  const [teleconsultData, setTeleconsultData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    fetchDashboard();
    fetchTeleconsultData();
  }, []);

  const fetchDashboard = async () => {
    try {
      const response = await apiClient.get('/dashboard');
      setData(response.data);
    } catch (error) {
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  const fetchTeleconsultData = async () => {
    try {
      const response = await apiClient.get('/teleconsult/dashboard-summary');
      setTeleconsultData(response.data);
    } catch (error) {
      console.error('Failed to load teleconsult data');
    }
  };

  const startTeleconsult = async (appointmentId, videoLink) => {
    try {
      const response = await apiClient.post(`/teleconsult/${appointmentId}/start`);
      if (videoLink) {
        window.open(videoLink, '_blank');
      }
      fetchTeleconsultData();
      fetchDashboard();
    } catch (error) {
      toast.error('Failed to start consultation');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-PH', {
      style: 'currency',
      currency: 'PHP',
      minimumFractionDigits: 0,
    }).format(amount || 0);
  };

  const PatientAvatar = ({ photo, name, size = 'sm' }) => {
    const sizeClass = size === 'lg' ? 'w-12 h-12' : 'w-10 h-10';
    return photo ? (
      <img 
        src={`data:image/png;base64,${photo}`} 
        alt={name}
        className={`${sizeClass} rounded-full object-cover border-2 border-white shadow-sm`}
      />
    ) : (
      <div className={`${sizeClass} rounded-full bg-primary flex items-center justify-center text-primary-foreground font-medium`}>
        {name?.charAt(0) || '?'}
      </div>
    );
  };

  return (
    <div className="space-y-8" data-testid="dashboard">
      {/* Header */}
      <div className="animate-fade-in">
        <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground">
          Magandang Araw, {user?.full_name?.split(' ')[0]}!
        </h1>
        <p className="text-muted-foreground mt-1">
          Here's what's happening at your clinic today
        </p>
      </div>

      {/* Today at a Glance */}
      <section className="animate-fade-in stagger-1">
        <h2 className="font-heading text-lg font-semibold mb-4 flex items-center gap-2">
          <Clock className="w-5 h-5 text-primary" strokeWidth={1.5} />
          Today at a Glance
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="card-hover cursor-pointer" onClick={() => navigate('/appointments')} data-testid="metric-patients-today">
            <CardContent className="p-4 md:p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="metric-value">{data?.today_at_a_glance?.patients_today || 0}</p>
                  <p className="metric-label">Patients Today</p>
                </div>
                <Users className="w-8 h-8 text-primary opacity-50" strokeWidth={1.5} />
              </div>
            </CardContent>
          </Card>

          <Card className="card-hover cursor-pointer" onClick={() => navigate('/appointments')} data-testid="metric-waiting">
            <CardContent className="p-4 md:p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="metric-value text-accent">{data?.today_at_a_glance?.waiting_now || 0}</p>
                  <p className="metric-label">Waiting Now</p>
                </div>
                <Clock className="w-8 h-8 text-accent opacity-50" strokeWidth={1.5} />
              </div>
            </CardContent>
          </Card>

          <Card className="card-hover cursor-pointer" onClick={() => navigate('/appointments')} data-testid="metric-appointments">
            <CardContent className="p-4 md:p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="metric-value">{data?.today_at_a_glance?.appointments_today || 0}</p>
                  <p className="metric-label">Appointments</p>
                </div>
                <CalendarCheck className="w-8 h-8 text-primary opacity-50" strokeWidth={1.5} />
              </div>
            </CardContent>
          </Card>

          {/* Next Patient */}
          <Card className="card-hover cursor-pointer col-span-2 md:col-span-1" onClick={() => navigate('/appointments')}>
            <CardContent className="p-4 md:p-6">
              {data?.today_at_a_glance?.next_patient ? (
                <div className="flex items-center gap-3">
                  <PatientAvatar 
                    photo={data.today_at_a_glance.next_patient.photo}
                    name={data.today_at_a_glance.next_patient.name}
                    size="lg"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{data.today_at_a_glance.next_patient.name}</p>
                    <p className="text-sm text-muted-foreground">{data.today_at_a_glance.next_patient.time}</p>
                    <Badge variant="outline" className="mt-1 text-xs">Next Patient</Badge>
                  </div>
                </div>
              ) : (
                <div className="text-center py-2">
                  <p className="text-muted-foreground text-sm">No patients waiting</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </section>

      {/* TeleConsult Today Widget */}
      {teleconsultData && (teleconsultData.upcoming_count > 0 || teleconsultData.in_progress_count > 0 || teleconsultData.missed_count > 0) && (
        <section className="animate-fade-in stagger-1">
          <h2 className="font-heading text-lg font-semibold mb-4 flex items-center gap-2">
            <Video className="w-5 h-5 text-blue-600" strokeWidth={1.5} />
            TeleConsult Today
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
            <Card className="border-blue-200 bg-blue-50/50">
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-blue-600">{teleconsultData.upcoming_count}</p>
                <p className="text-xs text-muted-foreground">Upcoming</p>
              </CardContent>
            </Card>
            <Card className="border-green-200 bg-green-50/50">
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-green-600">{teleconsultData.in_progress_count}</p>
                <p className="text-xs text-muted-foreground">In Progress</p>
              </CardContent>
            </Card>
            <Card className="border-red-200 bg-red-50/50">
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-red-600">{teleconsultData.missed_count}</p>
                <p className="text-xs text-muted-foreground">Missed</p>
              </CardContent>
            </Card>
            <Card className="border-emerald-200 bg-emerald-50/50">
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-emerald-600">{formatCurrency(teleconsultData.revenue_today || 0)}</p>
                <p className="text-xs text-muted-foreground">Revenue</p>
              </CardContent>
            </Card>
          </div>

          {/* Upcoming Teleconsults */}
          {teleconsultData.upcoming?.length > 0 && (
            <Card>
              <CardContent className="p-4">
                <p className="text-sm font-medium mb-3">Upcoming Online Consultations</p>
                <div className="space-y-2">
                  {teleconsultData.upcoming.map((tc, i) => (
                    <div 
                      key={tc.appointment_id} 
                      className="flex items-center gap-3 p-3 rounded-lg border hover:bg-muted/50 cursor-pointer"
                      data-testid={`teleconsult-upcoming-${tc.appointment_id}`}
                    >
                      <PatientAvatar photo={tc.patient_photo} name={tc.patient_name} />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{tc.patient_name}</p>
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          <Clock className="w-3 h-3" />
                          <span>{tc.time}</span>
                          {tc.symptoms && <span className="truncate">• {tc.symptoms}</span>}
                        </div>
                      </div>
                      {tc.payment_status === 'pending' && (
                        <Badge className="bg-amber-100 text-amber-800 text-xs">Unpaid</Badge>
                      )}
                      <Button 
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          startTeleconsult(tc.appointment_id);
                          navigate('/appointments');
                        }}
                      >
                        <Video className="w-4 h-4 mr-1" />
                        Start
                      </Button>
                    </div>
                  ))}
                </div>
                {teleconsultData.upcoming_count > 5 && (
                  <Button 
                    variant="ghost" 
                    className="w-full mt-2 text-blue-600"
                    onClick={() => navigate('/appointments')}
                  >
                    View All ({teleconsultData.upcoming_count})
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                )}
              </CardContent>
            </Card>
          )}

          {/* In Progress */}
          {teleconsultData.in_progress?.length > 0 && (
            <Card className="mt-4 border-green-200">
              <CardContent className="p-4">
                <p className="text-sm font-medium mb-3 text-green-700">Active Consultation</p>
                {teleconsultData.in_progress.map((tc) => (
                  <div 
                    key={tc.appointment_id}
                    className="flex items-center gap-3 p-3 rounded-lg bg-green-50 border border-green-200"
                  >
                    <div className="w-3 h-3 rounded-full bg-green-500 animate-pulse"></div>
                    <div className="flex-1">
                      <p className="font-medium">{tc.patient_name}</p>
                      <p className="text-xs text-muted-foreground">Started at {tc.start_time?.split('T')[1]?.slice(0, 5) || 'now'}</p>
                    </div>
                    <Button size="sm" variant="outline" onClick={() => navigate('/appointments')}>
                      <ExternalLink className="w-4 h-4 mr-1" />
                      View
                    </Button>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </section>
      )}

      {/* Two Column Layout */}
      <div className="grid lg:grid-cols-2 gap-8">
        {/* Left Column */}
        <div className="space-y-8">
          {/* Care in Motion */}
          <section className="animate-fade-in stagger-2">
            <h2 className="font-heading text-lg font-semibold mb-4 flex items-center gap-2">
              <Heart className="w-5 h-5 text-primary" strokeWidth={1.5} />
              Care in Motion
            </h2>
            <div className="grid grid-cols-2 gap-4">
              <Card className="card-hover cursor-pointer" onClick={() => navigate('/follow-ups')} data-testid="metric-followups-due">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-2xl font-bold">{data?.care_in_motion?.follow_ups_due_count || 0}</p>
                    <ChevronRight className="w-5 h-5 text-muted-foreground" />
                  </div>
                  <p className="text-sm text-muted-foreground">Follow-ups Due</p>
                </CardContent>
              </Card>

              <Card className="card-hover cursor-pointer" onClick={() => navigate('/follow-ups')} data-testid="metric-missed-followups">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-2xl font-bold text-destructive">{data?.care_in_motion?.missed_follow_ups_count || 0}</p>
                    <AlertCircle className="w-5 h-5 text-destructive" />
                  </div>
                  <p className="text-sm text-muted-foreground">Missed Follow-ups</p>
                </CardContent>
              </Card>
            </div>

            {/* Follow-up List */}
            {data?.care_in_motion?.follow_ups_due_list?.length > 0 && (
              <Card className="mt-4">
                <CardContent className="p-4">
                  <p className="text-sm font-medium mb-3">Due Today</p>
                  <div className="space-y-2">
                    {data.care_in_motion.follow_ups_due_list.slice(0, 3).map((f, i) => (
                      <div key={i} className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted/50 cursor-pointer">
                        <PatientAvatar photo={f.patient_photo} name={f.patient_name} />
                        <span className="text-sm flex-1 truncate">{f.patient_name}</span>
                        <Button size="sm" variant="ghost" className="h-8">
                          <Send className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </section>

          {/* Patient Memory */}
          <section className="animate-fade-in stagger-3">
            <h2 className="font-heading text-lg font-semibold mb-4 flex items-center gap-2">
              <UserCheck className="w-5 h-5 text-primary" strokeWidth={1.5} />
              Patient Memory
            </h2>
            <div className="grid grid-cols-3 gap-4">
              <Card className="card-hover cursor-pointer" onClick={() => navigate('/patients')}>
                <CardContent className="p-4 text-center">
                  <p className="text-2xl font-bold">{data?.patient_memory?.active_patients || 0}</p>
                  <p className="text-xs text-muted-foreground mt-1">Active Patients</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4 text-center">
                  <p className="text-2xl font-bold">{data?.patient_memory?.returning_rate || 0}%</p>
                  <p className="text-xs text-muted-foreground mt-1">Returning Rate</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4 text-center">
                  <p className="text-2xl font-bold">{data?.patient_memory?.records_with_rx_labs || 0}</p>
                  <p className="text-xs text-muted-foreground mt-1">RX/Lab Records</p>
                </CardContent>
              </Card>
            </div>
          </section>

          {/* Medicine Stewardship */}
          <section className="animate-fade-in stagger-4">
            <h2 className="font-heading text-lg font-semibold mb-4 flex items-center gap-2">
              <Pill className="w-5 h-5 text-primary" strokeWidth={1.5} />
              Medicine Stewardship
            </h2>
            <div className="grid grid-cols-2 gap-4">
              <Card className="card-hover cursor-pointer" onClick={() => navigate('/inventory?filter=expiring')} data-testid="metric-expiring">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center">
                      <AlertCircle className="w-5 h-5 text-accent" />
                    </div>
                    <div>
                      <p className="text-xl font-bold">{data?.medicine_stewardship?.expiring_6_months || 0}</p>
                      <p className="text-xs text-muted-foreground">Expiring in 6 months</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="card-hover cursor-pointer" onClick={() => navigate('/inventory?filter=lowstock')} data-testid="metric-lowstock">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-destructive/10 flex items-center justify-center">
                      <Package className="w-5 h-5 text-destructive" />
                    </div>
                    <div>
                      <p className="text-xl font-bold">{data?.medicine_stewardship?.low_stock || 0}</p>
                      <p className="text-xs text-muted-foreground">Low Stock</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </section>
        </div>

        {/* Right Column */}
        <div className="space-y-8">
          {/* Clinic Health */}
          <section className="animate-fade-in stagger-2">
            <h2 className="font-heading text-lg font-semibold mb-4 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-primary" strokeWidth={1.5} />
              Clinic Health
            </h2>
            <Card>
              <CardContent className="p-6">
                <div className="grid grid-cols-2 gap-6">
                  {/* Today */}
                  <div>
                    <p className="text-sm font-medium text-muted-foreground mb-4">Today</p>
                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="text-sm">Income</span>
                        <span className="font-medium text-primary">{formatCurrency(data?.clinic_health?.today?.income)}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm">Expenses</span>
                        <span className="font-medium text-destructive">{formatCurrency(data?.clinic_health?.today?.expenses)}</span>
                      </div>
                      <div className="pt-2 border-t flex justify-between items-center">
                        <span className="text-sm font-medium">Net</span>
                        <span className={`font-bold ${(data?.clinic_health?.today?.net || 0) >= 0 ? 'text-primary' : 'text-destructive'}`}>
                          {formatCurrency(data?.clinic_health?.today?.net)}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* This Month */}
                  <div>
                    <p className="text-sm font-medium text-muted-foreground mb-4">This Month</p>
                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="text-sm">Income</span>
                        <span className="font-medium text-primary">{formatCurrency(data?.clinic_health?.this_month?.income)}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm">Expenses</span>
                        <span className="font-medium text-destructive">{formatCurrency(data?.clinic_health?.this_month?.expenses)}</span>
                      </div>
                      <div className="pt-2 border-t flex justify-between items-center">
                        <span className="text-sm font-medium">Net</span>
                        <span className={`font-bold ${(data?.clinic_health?.this_month?.net || 0) >= 0 ? 'text-primary' : 'text-destructive'}`}>
                          {formatCurrency(data?.clinic_health?.this_month?.net)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Care Given */}
                <div className="mt-6 pt-4 border-t">
                  <div className="flex items-center justify-between bg-primary/5 rounded-lg p-4">
                    <div className="flex items-center gap-3">
                      <Heart className="w-6 h-6 text-primary" strokeWidth={1.5} />
                      <div>
                        <p className="text-sm font-medium">Care Given This Month</p>
                        <p className="text-xs text-muted-foreground">Pro Bono + Discounts</p>
                      </div>
                    </div>
                    <p className="text-xl font-bold text-primary">{formatCurrency(data?.clinic_health?.care_given_this_month)}</p>
                  </div>
                </div>

                {/* Receivables */}
                {((data?.clinic_health?.hmo_philhealth_outstanding || 0) + (data?.clinic_health?.credit_outstanding || 0)) > 0 && (
                  <div className="mt-4 pt-4 border-t">
                    <p className="text-sm font-medium text-muted-foreground mb-3">Outstanding Receivables</p>
                    <div className="grid grid-cols-2 gap-3">
                      <div 
                        className="flex items-center gap-3 p-3 rounded-lg bg-amber-50 border border-amber-200 cursor-pointer hover:bg-amber-100 transition-colors"
                        onClick={() => navigate('/receivables')}
                        data-testid="metric-hmo-outstanding"
                      >
                        <Building2 className="w-5 h-5 text-amber-600" />
                        <div>
                          <p className="text-sm font-bold text-amber-700">{formatCurrency(data?.clinic_health?.hmo_philhealth_outstanding)}</p>
                          <p className="text-xs text-amber-600">HMO/PhilHealth</p>
                        </div>
                      </div>
                      <div 
                        className="flex items-center gap-3 p-3 rounded-lg bg-blue-50 border border-blue-200 cursor-pointer hover:bg-blue-100 transition-colors"
                        onClick={() => navigate('/receivables')}
                        data-testid="metric-credit-outstanding"
                      >
                        <CreditCard className="w-5 h-5 text-blue-600" />
                        <div>
                          <p className="text-sm font-bold text-blue-700">{formatCurrency(data?.clinic_health?.credit_outstanding)}</p>
                          <p className="text-xs text-blue-600">Patient Credit</p>
                        </div>
                      </div>
                    </div>
                    {(data?.clinic_health?.overdue_count || 0) > 0 && (
                      <div 
                        className="mt-2 flex items-center justify-between p-2 rounded-lg bg-red-50 border border-red-200 cursor-pointer hover:bg-red-100 transition-colors"
                        onClick={() => navigate('/receivables')}
                      >
                        <div className="flex items-center gap-2">
                          <AlertCircle className="w-4 h-4 text-red-600" />
                          <span className="text-sm text-red-700">{data?.clinic_health?.overdue_count} overdue</span>
                        </div>
                        <ChevronRight className="w-4 h-4 text-red-600" />
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </section>

          {/* Compassion */}
          <section className="animate-fade-in stagger-3">
            <h2 className="font-heading text-lg font-semibold mb-4 flex items-center gap-2">
              <Heart className="w-5 h-5 text-primary" strokeWidth={1.5} />
              Compassion
            </h2>
            <div className="grid grid-cols-3 gap-4">
              <Card className="card-hover cursor-pointer" onClick={() => navigate('/patients?tag=pro_bono')}>
                <CardContent className="p-4 text-center">
                  <p className="text-2xl font-bold text-primary">{data?.compassion?.pro_bono_patients || 0}</p>
                  <p className="text-xs text-muted-foreground mt-1">Pro Bono</p>
                </CardContent>
              </Card>
              <Card className="card-hover cursor-pointer" onClick={() => navigate('/patients?tag=discount')}>
                <CardContent className="p-4 text-center">
                  <p className="text-2xl font-bold text-accent">{data?.compassion?.discount_patients || 0}</p>
                  <p className="text-xs text-muted-foreground mt-1">Discounted</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4 text-center">
                  <p className="text-2xl font-bold">{data?.compassion?.total_helped_this_month || 0}</p>
                  <p className="text-xs text-muted-foreground mt-1">Total Helped</p>
                </CardContent>
              </Card>
            </div>
          </section>

          {/* Referral Confidence */}
          <section className="animate-fade-in stagger-4">
            <h2 className="font-heading text-lg font-semibold mb-4 flex items-center gap-2">
              <Users className="w-5 h-5 text-primary" strokeWidth={1.5} />
              Referral Confidence
            </h2>
            <Card className="card-hover cursor-pointer" onClick={() => navigate('/directory')}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-2xl font-bold">{data?.referral_confidence?.doctors_you_trust || 0}</p>
                    <p className="text-sm text-muted-foreground">Doctors You Trust</p>
                  </div>
                  <ChevronRight className="w-5 h-5 text-muted-foreground" />
                </div>
              </CardContent>
            </Card>
            
            {data?.referral_confidence?.recent_referrals?.length > 0 && (
              <Card className="mt-4">
                <CardContent className="p-4">
                  <p className="text-sm font-medium mb-3">Recent Referrals</p>
                  <div className="space-y-2">
                    {data.referral_confidence.recent_referrals.slice(0, 3).map((r, i) => (
                      <div key={i} className="flex items-center justify-between p-2 rounded-lg bg-muted/50">
                        <span className="text-sm truncate flex-1">{r.patient_name}</span>
                        <span className="text-xs text-muted-foreground ml-2">to {r.doctor_name}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </section>
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;
